import React, { createContext, useContext, useState, useEffect } from 'react';

export const personas = [
  {
    id: 'student',
    name: 'Student',
    icon: '🎓',
    description: 'Academic innovation, thesis, research projects',
    color: 'from-blue-500 to-cyan-500',
    metrics: ['Academic Impact', 'Innovation Score', 'Research Quality']
  },
  {
    id: 'researcher',
    name: 'Researcher',
    icon: '🔬',
    description: 'Scientific breakthroughs, publications, discoveries',
    color: 'from-purple-500 to-pink-500',
    metrics: ['Novelty Index', 'Citation Potential', 'Breakthrough Score']
  },
  {
    id: 'scientist',
    name: 'Scientist',
    icon: '🧪',
    description: 'Lab experiments, hypothesis testing, empirical discovery',
    color: 'from-teal-500 to-cyan-500',
    metrics: ['Scientific Rigor', 'Reproducibility', 'Discovery Potential']
  },
  {
    id: 'consultant',
    name: 'Consultant',
    icon: '💼',
    description: 'Client solutions, business strategy, frameworks',
    color: 'from-green-500 to-emerald-500',
    metrics: ['Business Value', 'Implementation Feasibility', 'ROI Potential']
  },
  {
    id: 'innovator',
    name: 'Innovator',
    icon: '🚀',
    description: 'Startups, product development, market disruption',
    color: 'from-orange-500 to-red-500',
    metrics: ['Market Potential', 'Disruption Score', 'Scalability']
  },
  {
    id: 'analyst',
    name: 'Analyst',
    icon: '📊',
    description: 'Data-driven insights, market research, trends',
    color: 'from-indigo-500 to-violet-500',
    metrics: ['Data Quality', 'Insight Depth', 'Actionability']
  },
  {
    id: 'design-thinker',
    name: 'Design Thinker',
    icon: '💡✨',
    description: 'Human-centered design, empathy-driven solutions, prototyping',
    color: 'from-pink-500 to-rose-500',
    metrics: ['User Empathy', 'Design Innovation', 'Iterative Quality']
  },
  {
    id: 'social-innovation',
    name: 'Social Innovator',
    icon: '🌍',
    description: 'Solving global challenges, UN SDGs, sustainable impact',
    color: 'from-green-500 to-emerald-500',
    metrics: ['Social Impact', 'Scalability', 'Sustainability']
  }
];

const PersonaContext = createContext();

export const PersonaProvider = ({ children }) => {
  const [selectedPersonas, setSelectedPersonas] = useState([]);
  const [sessionPersonas, setSessionPersonas] = useState([]);
  const [showPersonaModal, setShowPersonaModal] = useState(false);

  useEffect(() => {
    // Load saved personas but don't auto-show modal
    const savedPersonas = localStorage.getItem('if_personas');
    if (savedPersonas) {
      setSelectedPersonas(JSON.parse(savedPersonas));
    }
  }, []);

  const openPersonaModal = () => {
    setShowPersonaModal(true);
  };

  const closePersonaModal = () => {
    setShowPersonaModal(false);
  };

  const selectPersonas = (personaIds, isDefault = true) => {
    if (isDefault) {
      setSelectedPersonas(personaIds);
      localStorage.setItem('if_personas', JSON.stringify(personaIds));
    }
    setSessionPersonas(personaIds);
  };

  const togglePersona = (personaId) => {
    const current = [...selectedPersonas];
    const index = current.indexOf(personaId);
    
    if (index > -1) {
      // Remove if already selected
      current.splice(index, 1);
    } else {
      // Add if not selected
      current.push(personaId);
    }
    
    setSelectedPersonas(current);
    localStorage.setItem('if_personas', JSON.stringify(current));
  };

  const getActivePersonas = () => {
    return sessionPersonas.length > 0 ? sessionPersonas : selectedPersonas;
  };

  const getPersonaData = (personaId) => {
    return personas.find(p => p.id === personaId);
  };

  const getAllActivePersonasData = () => {
    return getActivePersonas().map(id => getPersonaData(id)).filter(Boolean);
  };

  return (
    <PersonaContext.Provider value={{ 
      selectedPersonas,
      sessionPersonas,
      showPersonaModal,
      setShowPersonaModal,
      openPersonaModal,
      closePersonaModal,
      selectPersonas,
      togglePersona,
      getActivePersonas,
      getPersonaData,
      getAllActivePersonasData,
      personas 
    }}>
      {children}
    </PersonaContext.Provider>
  );
};

export const usePersona = () => {
  const context = useContext(PersonaContext);
  if (!context) {
    throw new Error('usePersona must be used within PersonaProvider');
  }
  return context;
};